import React, { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, Divider, ListItem, useOverlay } from '@fs/zion-ui'
import {
  ContentDelete,
  ControlGrid,
  SocialChat,
  ContentFlag,
  ContentHistory,
  ContentPrint,
  PersonDuplicates,
  PersonFamily,
  PersonSearch,
} from '@fs/zion-icon'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { getFSTreesUrl } from '@fs/zion-search-partners'
import { usePermission } from '@fs/zion-permissions'
import { useFeatureFlag } from '@fs/zion-flags'
import { css } from '@emotion/core'
import { usePersonPageParams } from '../../personPageRouteUtil'
import useFocusPerson from '../../../data-service/useFocusPerson'
import { usePersonNotes } from '../../../providers/NotesProvider'
import { useResearchHelpContext } from '../../contexts/ResearchHelpContext'
import { useFocusFamilyMembersContext } from '../../contexts/FocusFamilyMembersContext'
import useDeleteConstraint from '../../../data-service/useDeleteConstraint'
import PossibleDuplicatesNotAvailableDialog from './PossibleDuplicatesNotAvailableDialog'

const ReportAbuseOverlay = lazy(() => import('@fs/zion-report-abuse-overlay'))
const MergeByIdDialog = lazy(() => import('./MergeByIdDialog'))
const DeletePersonDialog = lazy(() => import('./DeletePersonDialog'))
const PrintDialog = lazy(() => import('./PrintDialog'))

const redCss = css`
  color: ${colors.danger50};
`

type Props = {
  /**
   * Namespaces this copy's item test ids. `MenuOverlay` keeps its children mounted and only toggles
   * `display`, so every copy of this list is in the DOM at once. The banner omits this and keeps the
   * ids acceptance tests already target; the mini header passes its own so the two don't collide.
   */
  testIdPrefix?: string
}

export default function SourceCentricToolsMenuItems({ testIdPrefix }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { pid } = usePersonPageParams('full')

  const possibleDuplicatesNotAvailableOverlay = useOverlay()
  const mergeByIdOverlay = useOverlay()
  const reportAbuseOverlay = useOverlay()
  const deletePersonOverlay = useOverlay()
  const printOverlay = useOverlay()

  const { deleteConstraint } = useDeleteConstraint(pid)
  const canDelete = deleteConstraint && deleteConstraint.personDeleteConstraint === 'CAN_DELETE_PERSON'

  const [canModifyPrivateSpaceEntities] = usePermission('ModifyAllPrivateSpaceEntitiesPermission')
  const showAdvancedUnmergeButton =
    useFeatureFlag('treeWeb_personR9_advancedUnmerge').isOn && canModifyPrivateSpaceEntities
  const showSourceGrid = useFeatureFlag('treeWeb_personR9_sourceGrid').isOn

  const { researchHelpData } = useResearchHelpContext()
  const { isResearcherGroup } = useGroupsContext()
  const { focusPerson } = useFocusPerson()
  const { focusFamilyMembers } = useFocusFamilyMembersContext()
  const { notes } = usePersonNotes()
  if (!focusPerson) return null

  const testId = (id: string): string => {
    if (!testIdPrefix) return id
    return `${testIdPrefix}-${id}`
  }

  const possibleDuplicatesCount = researchHelpData.duplicatePersonDataProblems?.length ?? 0

  return (
    <>
      <ListItem
        data-testid={testId('tool-changelog')}
        linkName="Tools: Latest Changes"
        to={`/changelog/${pid}`}
        startElement={<ListItem.Icon Icon={ContentHistory} />}
        primaryText={t('tree-person.changelog-summary.label')}
        dense
      />

      {!focusPerson.readOnly && (
        <ListItem
          data-testid={testId('tool-possible-duplicates')}
          linkName="Tools: Possible Duplicates"
          onClick={
            !focusPerson.visibleToAllWhenUsingFamilySearchApps
              ? possibleDuplicatesNotAvailableOverlay.handleClick
              : undefined
          }
          to={focusPerson.visibleToAllWhenUsingFamilySearchApps ? `/research-help/${pid}` : undefined}
          startElement={<ListItem.Icon Icon={PersonDuplicates} />}
          primaryText={`${t('tree-person.possible-duplicates.label')} (${possibleDuplicatesCount})`}
          dense
        />
      )}

      <ListItem
        data-testid={testId('tool-find-similar-people')}
        disabled={isResearcherGroup}
        linkName="Tools: Find Similar People"
        to={getFSTreesUrl({ person: focusPerson, familyMembers: focusFamilyMembers })}
        external
        target="_blank"
        startElement={<ListItem.Icon Icon={PersonSearch} />}
        primaryText={t('tree-person.find-similar-people.label')}
        dense
      />

      <ListItem
        data-testid={testId('tool-merge-by-id')}
        linkName="Tools: Merge By ID"
        onClick={mergeByIdOverlay.handleClick}
        startElement={<ListItem.Icon Icon={PersonFamily} />}
        primaryText={t('tree-person.merge-by-id.label')}
        disabled={focusPerson.readOnly}
        dense
      />

      {showAdvancedUnmergeButton && (
        <ListItem
          data-testid={testId('tool-advanced-unmerge')}
          linkName="Tools: Advanced Unmerge"
          to={`/unmerge/${pid}`}
          startElement={<ListItem.Icon Icon={PersonDuplicates} />}
          primaryText={t('tree-person.advanced-unmerge.label')}
          dense
        />
      )}

      <ListItem
        data-testid={testId('tool-notes')}
        linkName="Tools: Notes"
        to={`/notes/${pid}`}
        startElement={<ListItem.Icon Icon={SocialChat} />}
        primaryText={t('tree-person.notes-count.label', { count: notes?.length ?? 0 })}
        dense
      />

      {showSourceGrid && (
        <ListItem
          data-testid="tool-source-grid"
          linkName="Tools: Source Grid"
          to={`/source-grid/${pid}`}
          startElement={<ListItem.Icon Icon={ControlGrid} />}
          primaryText={t('tree-person.source-grid.label')}
          dense
        />
      )}

      {/* Ask AI Research Assistant */}

      <Divider marginY="nano" />

      <ListItem
        data-testid={testId('tool-print')}
        linkName="Tools: Print"
        onClick={printOverlay.handleClick}
        startElement={<ListItem.Icon Icon={ContentPrint} />}
        primaryText={t('tree-person.print-options.label')}
        dense
      />

      {/* TODO Admin - Export to Markdown */}

      <ListItem
        data-testid={testId('tool-report-abuse')}
        linkName="Tools: Report Abuse"
        onClick={reportAbuseOverlay.handleClick}
        startElement={<ListItem.Icon Icon={ContentFlag} />}
        primaryText={t('tree-person.report-abuse.label')}
        dense
      />

      <Divider marginY="nano" />

      <ListItem
        data-testid={testId('tool-delete-person')}
        color="error"
        disabled={!canDelete}
        linkName="Tools: Delete Person"
        onClick={deletePersonOverlay.handleClick}
        startElement={<ListItem.Icon Icon={ContentDelete} color={canDelete ? colors.danger50 : undefined} />}
        primaryText={<span css={canDelete ? redCss : undefined}>{t('tree-person.delete-person.label')}</span>}
        dense
      />

      <Suspense fallback="">
        {(mergeByIdOverlay.isOpen || mergeByIdOverlay.transitioning) && <MergeByIdDialog overlay={mergeByIdOverlay} />}
        {(deletePersonOverlay.isOpen || deletePersonOverlay.transitioning) && (
          <DeletePersonDialog overlay={deletePersonOverlay} />
        )}
        {(possibleDuplicatesNotAvailableOverlay.isOpen || possibleDuplicatesNotAvailableOverlay.transitioning) && (
          <PossibleDuplicatesNotAvailableDialog overlay={possibleDuplicatesNotAvailableOverlay} />
        )}
        {(reportAbuseOverlay.isOpen || reportAbuseOverlay.transitioning) && (
          <ReportAbuseOverlay
            overlay={reportAbuseOverlay}
            subject={`Person Details: ${focusPerson?.name} | ${pid}`}
            additionalInfo={`
      Person ID: ${pid}
      Person Name: ${focusPerson?.name}
      URL: ${window.location.href}
      `}
          />
        )}
        {(printOverlay.isOpen || printOverlay.transitioning) && <PrintDialog overlay={printOverlay} />}
      </Suspense>
    </>
  )
}
